'use client';

import { FC, useState } from 'react';

import { Price } from '@ravago/shared/page-data/models/elements';
import { ShoppingCartItem, ShoppingCartItemWithOptionalSlug, ShoppingCartProblem } from '@ravago/shared/page-data/models/features';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { ShoppingCartRemoveItemDialogElement } from './shopping-cart-remove-item-dialog-element';

interface Props {
   shoppingCartItem: ShoppingCartItemWithOptionalSlug;
   removeFromShoppingCart: (shoppingCartItemId: ShoppingCartItem['id']) => void;
   locale: string;
}

export const ShoppingCartItemElement: FC<Props> = ({ shoppingCartItem, removeFromShoppingCart, locale }) => {
   const [dialogOpen, setDialogOpen] = useState(false);
   const translate = useTranslations();
   const format = useFormatter();

   type ProblemLevel = 'ERROR' | 'WARNING' | 'INFORMATION';
   type AlertVariant = 'error' | 'warning' | 'info';

   const variantMapping: Record<ProblemLevel, AlertVariant> = {
      ERROR: 'error',
      WARNING: 'warning',
      INFORMATION: 'info'
   };

   const hasNotFixedPrice = shoppingCartItem.sourceDetails?.isPriceFixed === false;

   const totalPrice: Price = {
      amount: shoppingCartItem.unitPrice.price.amount * shoppingCartItem.quantity.amount,
      currency: shoppingCartItem.unitPrice.price.currency
   };

   const problems = shoppingCartItem.problems.map((problem: ShoppingCartProblem) => (
      <Alert
         headline={problem.level.charAt(0) + problem.level.substring(1, problem.level.length).toLowerCase()}
         key={problem.code}
         variant={variantMapping[problem.level]}
      >
         {translate(problem.code)}
      </Alert>
   ));
   return (
      <Paper data-testid="shopping-cart__shopping-cart-item" shadow>
         <Box direction="column" gap="md">
            <Box items="center" justify="between">
               <Link
                  disabled={!shoppingCartItem.slug}
                  underline={!shoppingCartItem.slug ? 'none' : 'always'}
                  href={!shoppingCartItem.slug ? '' : `/${locale}/products/${shoppingCartItem.slug}`}
               >
                  <Typography variant="body-highlight" color={!shoppingCartItem.slug ? 'subtle' : 'inherit'}>
                     {shoppingCartItem.product.description}
                  </Typography>
               </Link>

               <Typography align="right">
                  <IconButton onClick={() => setDialogOpen(true)} icon="trash" ariaLabel={`Remove ${shoppingCartItem.product.description}`} />
               </Typography>
            </Box>

            <Box justify="between" items="end">
               <Box gap="xs" direction="column">
                  <Box items="baseline">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.quantity')}
                     </Typography>
                     <Typography data-testid="shopping-cart__quantity" variant="small" component="span">
                        {format.quantity(shoppingCartItem.quantity)}
                     </Typography>
                  </Box>
                  <Box items="baseline">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.packaging')}
                     </Typography>
                     <Typography data-testid="shopping-cart__quantity" variant="small" component="span">
                        {shoppingCartItem.packaging}
                     </Typography>
                  </Box>
                  <Box items="baseline">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.product-reference')}
                     </Typography>
                     <Typography data-testid="shopping-cart__quantity" variant="small" component="span">
                        {shoppingCartItem.customerProductReference}
                     </Typography>
                  </Box>
               </Box>
               <Box justify="between">
                  <Box direction="column" gap="none">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.unit-price')}:
                     </Typography>
                     <Typography data-testid="shopping-cart__unit-price" color="primary" component="h4" variant="body-highlight">
                        {format.unitPrice(shoppingCartItem.unitPrice)}
                        {hasNotFixedPrice && '*'}
                     </Typography>
                  </Box>
                  <Box direction="column" gap="none">
                     <Typography variant="caption" component="span">
                        {translate('shopping-cart.total-price')}:
                     </Typography>
                     <Typography data-testid="shopping-cart__total-price" color="primary" component="h4" variant="body-highlight">
                        {format.price({
                           amount: totalPrice.amount,
                           currency: totalPrice.currency
                        })}
                        {hasNotFixedPrice && '*'}
                     </Typography>
                  </Box>
               </Box>
            </Box>
            {hasNotFixedPrice && (
               <Typography variant="caption" color="subtle">
                  *
                  {shoppingCartItem.sourceDetails?.marketPricingMonth
                     ? translate('commodity-pricing.price-contingent-to-month-index', {
                          month: format.fullMonthName(shoppingCartItem.sourceDetails.marketPricingMonth)
                       })
                     : translate('commodity-pricing.to-be-confirmed')}
               </Typography>
            )}
            {problems}
         </Box>
         <ShoppingCartRemoveItemDialogElement
            open={dialogOpen}
            shoppingCartItem={shoppingCartItem}
            removeFromShoppingCart={removeFromShoppingCart}
            onDialogClose={() => setDialogOpen(false)}
         />
      </Paper>
   );
};
